<template>
<div
    @click.self="$emit('close')"
    class="flex fixed inset-0 items-center justify-center bg-black/50 backdrop-blur-sm z-40 p-4">
    <div class="gloss-glass relative p-6 w-full max-w-2xl max-h-[70vh] flex flex-col m-auto text-center z-50">

        <!-- Header -->
        <div class="mb-2 text-base flex space-x-2 justify-between items-center shrink-0">
            <span class="font-semibold text-left">Are you sure you want to {{ action }} the following?</span>
            <button
                @click="$emit('close')"
                class="bg-gray-300 hover:bg-gray-400 dark:hover:bg-gray-800 text-black dark:text-white px-2 py-2 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="lucide lucide-x">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                </svg>
            </button>
        </div>

        <!-- Child object view -->
        <div v-if="isChildObject" class="overflow-auto w-full bg-gray-100 dark:bg-gray-700 rounded-lg">
            <div class="my-6">
                <p v-for="item in count" class="font-semibold justify-left flex pl-4">{{ item.name }}: {{ item.value }}</p>
                <p v-for="item in componetObjects" class="font-medium justify-left flex pl-4">{{ item.value }}</p>
            </div>
        </div>

        <!-- Parent object view -->
        <div v-else class="overflow-auto flex-1 min-h-0 w-full bg-gray-100 dark:bg-gray-700 rounded-lg">
            <p class="font-semibold mt-2">All of the following related items will be deleted:</p>
            <div class="my-6" v-for="(item, index) in componetObjects" :key="index">
                <div v-for="(object, i) in item.values" :key="i">
                    <p v-if="object.value !== 0 && object.value !== null" class="font-semibold flex items-start pl-4">
                        <strong v-if="parentObjects.has(object.name)">{{ object.name }}: {{ object.value }}</strong>
                        <span v-else class="font-medium">{{ object.name }}: {{ object.value }}</span>
                    </p>
                </div>
            </div>
        </div>

        <!-- Loading bar -->
        <div v-if="isDeleting" class="w-full shrink-0 h-0.5 bg-gray-300 dark:bg-gray-700">
            <div class="h-full bg-red-500 animate-[loading_2s_ease-in-out_infinite]"></div>
        </div>

        <!-- Actions -->
        <div class="text-base flex space-x-2 justify-center mt-6 shrink-0">
            <button
                class="rounded-md bg-transparent hover:bg-transparent focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
                :class="action === 'delete' ? 'text-gray-500 dark:text-white hover:text-red-500' : 'text-gray-500 dark:text-white hover:text-green-600'"
                @click="$emit('delete')">
                <!-- Check icon for enable/disable -->
                <svg v-if="action !== 'delete'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6 9 17l-5-5"/>
                </svg>
                <!-- Lucide trash icon for delete -->
                <Trash2 v-else/>
            </button>
            <button
                class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @click="$emit('close')">
                Cancel
            </button>
        </div>

    </div>
</div>
</template>

<script>
import { Trash2 } from 'lucide-vue-next';
export default {
    emits: ['close', 'delete'],
    components: { Trash2 },
    props: {
        deletingItem: { type: Boolean, default: false },
        componetObjects: { type: Array, default: () => [] },
        isDeleting: { type: Boolean, default: false },
        isChildObject: { type: Boolean, default: false },
        count: { type: Array, default: () => [] },
        action: { type: String, default: "delete" }
    },
    data() {
        return {
            parentObjects: new Set(['Site', 'Probe', 'Division', 'Organisation'])
        }
    }
}
</script>

<style scoped>
.gloss-glass {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 1rem;
    backdrop-filter: blur(16px) saturate(140%);
    -webkit-backdrop-filter: blur(16px) saturate(140%);
    border: 1px solid rgba(255, 255, 255, 0.45);
    box-shadow:
        0 20px 60px -10px rgba(0, 0, 0, 0.25),
        0 0 0 1px rgba(255, 255, 255, 0.06),
        inset 0 1px 0 0 rgba(255, 255, 255, 0.7),
        inset 0 -1px 0 0 rgba(255, 255, 255, 0.1);
    overflow: hidden;
}

.gloss-glass::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.25) 0%,
        rgba(255, 255, 255, 0) 22%
    );
    pointer-events: none;
    z-index: 0;
}

.gloss-glass > * {
    position: relative;
    z-index: 1;
}

@media (prefers-color-scheme: dark) {
    .gloss-glass {
        background-color: rgba(31, 41, 55, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.12);
        box-shadow:
            0 20px 60px -10px rgba(0, 0, 0, 0.6),
            0 0 0 1px rgba(255, 255, 255, 0.04),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.12),
            inset 0 -1px 0 0 rgba(255, 255, 255, 0.03);
    }

    .gloss-glass::before {
        background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.06) 0%,
            rgba(255, 255, 255, 0) 22%
        );
    }
}
</style>